import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

interface HoldButtonProps {
  className?: string;
  label?: string;
  icon?: string;
  ariaLabel: string;
  vibrate?: boolean;
  onDown: () => void;
  onUp: () => void;
  children?: ReactNode;
}

/**
 * Knopf, der solange "gedrueckt" bleibt, wie der Finger liegt - inklusive
 * Pointer-Capture, damit ein abrutschender Finger nicht Vollgas festhaengen
 * laesst. Genau der Fehler, an dem Kinderspiele sonst scheitern.
 */
export function HoldButton({
  className = '',
  label,
  icon,
  ariaLabel,
  vibrate = false,
  onDown,
  onUp,
  children,
}: HoldButtonProps): JSX.Element {
  const ref = useRef<HTMLButtonElement>(null);
  const activePointer = useRef<number | null>(null);

  const press = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (activePointer.current !== null) return;
      activePointer.current = e.pointerId;
      ref.current?.setPointerCapture(e.pointerId);
      ref.current?.classList.add('is-pressed');
      if (vibrate && 'vibrate' in navigator) navigator.vibrate(8);
      onDown();
    },
    [onDown, vibrate],
  );

  const release = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (activePointer.current !== e.pointerId) return;
      activePointer.current = null;
      if (ref.current?.hasPointerCapture(e.pointerId)) {
        ref.current.releasePointerCapture(e.pointerId);
      }
      ref.current?.classList.remove('is-pressed');
      onUp();
    },
    [onUp],
  );

  return (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      className={`btn ${className}`}
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      onLostPointerCapture={release}
      onContextMenu={(e) => e.preventDefault()}
    >
      {icon ? <span className="icon" aria-hidden="true">{icon}</span> : null}
      {label ? <span>{label}</span> : null}
      {children}
    </button>
  );
}
